<script>
  import { mapState } from 'vuex';
  import { computed } from '@nuxtjs/composition-api';
  import throttle from 'lodash.throttle';
  import DropdownFilter from '@/components/DropdownFilter';
  import { productFilters } from '@/constants/products';
  import { sortLevelsList } from '@/utils/utils';
  import FilterModal from '~/components/FilterModal.vue';

  export default {
    components: {
      DropdownFilter,
      FilterModal
    },
    props: {
      availableFilters: {
        type: Array,
        default: () => []
      },
      defaultOptions: {
        type: Object,
        default: () => ({})
      },
      filters: {
        type: Object,
        default: () => ({})
      },
      currentPage: {
        type: Number,
        default: 1
      },
      loading: {
        type: Boolean,
        default: false
      },
      loadingFor: {
        type: String,
        default: ''
      }
    },

    setup (props, { emit }) {
      const setFilter = (filter) => {
        return (options) => {
          emit('filtered', {
            trigger: filter,
            value: {
              ...props.filters,
              [filter]: Array.isArray(options)
                ? options
                : [options]
            }
          });
        };
      };

      const getFilter = (filter) => {
        return computed(() => {
          return props.filters[filter] || [];
        });
      };

      const getFilterOptions = (filter) => {
        return computed(() => {
          return props.availableFilters.find((availableFilter) => {
            return availableFilter.id === filter;
          })?.options.map(item => item.id) || [];
        });
      };

      const getLoadingStateForFilter = (filter) => {
        return computed(() => {
          return props.loading && props.loadingFor === filter;
        });
      };

      const hasDefaultFilter = (attribute) => {
        if (!props.defaultOptions.filters) {
          return false;
        }
        return !!props.defaultOptions.filters[attribute]?.length;
      };

      const loadingSubjects = getLoadingStateForFilter(productFilters.subject);
      const selectedSubjects = getFilter(productFilters.subject);
      const subjects = getFilterOptions(productFilters.subject);
      const setSubjects = setFilter(productFilters.subject);

      const loadingLevels = getLoadingStateForFilter(productFilters.educationType);
      const selectedLevels = getFilter(productFilters.educationType);

      // `getFilterOptions` returns a computed ref; reading `.value` once at setup
      // froze `levels` to the (empty) initial facet list, so the schoolniveau
      // filter never populated. Keep it reactive.
      const levelOptions = getFilterOptions(productFilters.educationType);
      const levels = computed(() => sortLevelsList(levelOptions.value));
      const setLevels = setFilter(productFilters.educationType);

      const loadingProductTypes = getLoadingStateForFilter(productFilters.brand);
      const selectedProductTypes = getFilter(productFilters.brand);
      const productTypes = getFilterOptions(productFilters.brand);
      const setProductTypes = setFilter(productFilters.brand);

      return {
        hasDefaultFilter,
        setLevels,
        setSubjects,
        setProductTypes,
        selectedLevels,
        selectedSubjects,
        selectedProductTypes,
        loadingLevels,
        loadingSubjects,
        loadingProductTypes,
        levels,
        subjects,
        productTypes
      };
    },
    data () {
      return {
        prevScroll: 0,
        fixedHeader: false,
        topOffset: 0,
        containerHeight: 0,
        scrollTrigger: 0,
        filterDefinitions: productFilters,
        hasSalesNotice: true,
        activeFilterSubmenu: null,
        windowWidth: 0
      };
    },
    computed: {
      ...mapState({
        modalName: state => state.modal.name,
        productFiltersOpen: state => state.productFiltersOpen
      }),
      classes () {
        return {
          'md:py-6': this.fixedHeader,
          'md:py-10': !this.fixedHeader,
          'is-fixed': this.fixedHeader
        };
      },
      getOffsetCss () {
        return 'top: ' + (this.fixedHeader ? this.topOffset : 0) + 'px';
      },
      getContainerCss () {
        return this.fixedHeader ? 'height: ' + this.containerHeight + 'px' : '';
      },
      link () {
        return '/over-examenbundel'
      },
      activeFilters () {
        // Read through `productFilters` rather than hardcoded keys: these still
        // referenced the pre-v2 `examen_*` attribute names, so the summary of
        // active filters stayed empty no matter what was selected.
        return [
          productFilters.subject,
          productFilters.brand,
          productFilters.educationType
        ]
          .flatMap(key => this.filters[key] || [])
          .join(', ');
      },
      showMobileFilters () {
        // Show mobile filters on screens smaller than 768px (md breakpoint)
        return this.windowWidth < 768;
      }
    },
    watch: {
      modalName (v) {
        const offset = window.pageYOffset;
        if (v && offset > this.scrollTrigger) {
          document.removeEventListener('scroll', this.handleScroll);
          this.fixedHeader = true;
        } else {
          document.addEventListener('scroll', this.handleScroll);
        }
      },
      productFiltersOpen (isOpen) {
        if (isOpen && typeof window !== 'undefined') {
          this.scrollPosition = window.pageYOffset;
          const $body = document.querySelector('body');
          const bodyPaddingRight = `${window.innerWidth - document.body.clientWidth}px`;
          $body.style.paddingRight = bodyPaddingRight;
          $body.style.overflow = 'hidden';
          $body.style.position = 'fixed';
          $body.style.top = `-${this.scrollPosition}px`;
          $body.style.width = '100%';
        }
        if (!isOpen && typeof window !== 'undefined') {
          const $body = document.querySelector('body');
          $body.style.removeProperty('padding-right');
          $body.style.removeProperty('overflow');
          $body.style.removeProperty('position');
          $body.style.removeProperty('top');
          $body.style.removeProperty('width');
          window.scrollTo(0, this.scrollPosition);
        }
      }

    },
    mounted () {
      this.windowWidth = window.innerWidth;
      this.containerHeight = document.getElementsByClassName('product-filters')[0]?.offsetHeight;
      this.scrollTrigger = document.getElementsByClassName('product-filters')[0]?.getBoundingClientRect().top - 88 + document.documentElement.scrollTop;
      this.handleScroll();
      window.addEventListener('scroll', this.handleScroll);
      window.addEventListener('resize', this.handleResize);
      document.addEventListener('dismiss_sale_notice', this.updateSalesNotice);
      this.$nextTick(() => {
        this.calculateTopOffset();
      });
    },
    beforeDestroy () {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('dismiss_sale_notice', this.updateSalesNotice);
    },
    methods: {
      startFiltering (filterType, options) {
        this.activeFilterSubmenu = null;
        this.$store.commit('SET_FILTERING_OPEN', false);

        // Call the appropriate filter function based on the filterType
        switch (filterType) {
        case 'levels':
          return this.setLevels(options);
        case 'subjects':
          return this.setSubjects(options);
        case 'productTypes':
          return this.setProductTypes(options);
        default:
          console.warn('Unknown filter type:', filterType);
        }
      },
      handleResize () {
        this.windowWidth = window.innerWidth;
        this.calculateTopOffset();
      },
      handleSubmenuClick (id) {
        this.activeFilterSubmenu = this.activeFilterSubmenu === id ? null : id;
      },
      handleClick () {
        this.$store.commit('SET_FILTERING_OPEN', !this.productFiltersOpen);
      },
      updateSalesNotice () {
        this.hasSalesNotice = false;
        this.calculateTopOffset(true);
      },
      calculateTopOffset (ignoreSalesNotice = false) {
        let offset = document.getElementsByClassName('header-content')[0]?.offsetHeight ?? 0;
        if (!ignoreSalesNotice && this.hasSalesNotice) {
          offset += document.getElementsByClassName('sales-notice')[0]?.offsetHeight ?? 0;
        }
        this.topOffset = offset;
      },

      clear () {
        // Emit a single filtered event that clears all filters at once
        this.$emit('filtered', {
          trigger: 'clear-all',
          value: {
            [productFilters.educationType]: [],
            [productFilters.subject]: [],
            [productFilters.brand]: []
          }
        });
        
        setTimeout(() => {
          this.handleClick(false);
        }, 800);
      },
      handleScroll: throttle(function () {
        if (this.menuOpen) {
          this.prevScroll = Math.abs(parseInt(document.body.style.top, 10)) + 1;
          return;
        }
        const offset = window.pageYOffset;
        this.fixedHeader =  offset > this.scrollTrigger;
      }, 60),
    }
  };
</script>

<template>
  <div
    class="product-filters-holder"
    :style="getContainerCss"
  >
    <div
      class="left-0 z-20 w-full bg-white product-filters"
      :class="classes"
      :style="getOffsetCss"
    >
      <div class="container px-4 md:px-8">
        <div class="flex flex-wrap items-center justify-between gap-y-2 md:justify-start md:gap-y-0">
          <div class="flex flex-col w-full md:hidden gap-y-2 md:flex-row md:gap-y-0 md:gap-x-4 xl:gap-x-6">
            <e-button
              type="secondary"
              class="filter-button"
              @click="handleClick()"
            >
              <q-icon
                name="filter"
                class="mr-2"
              />
              Filter
            </e-button>
            <div v-show="activeFilters">
              <span class="text-xs text-blue-500">
                Gefilterd op: <span class="font-bold">
                  {{ activeFilters }}
                </span>
              </span>
            </div>
          </div>
          <div
            v-if="showMobileFilters"
            :class="productFiltersOpen ? 'block' : 'hidden'"
            class="fixed top-0 bottom-0 left-0 right-0 bg-white filter-wrapper"
          >
            <div class="flex flex-col justify-between h-screen"> 
              <div> 
                <div class="px-4 pt-4.5 pb-4 shadow flex justify-between items-center">
                  <h3 class="text-1xl leading-1">
                    Filter
                  </h3>
                  <e-button
                    type="tertiary"
                    aria-label="Sluiten"
                    class="filter-close-button"
                    @click="handleClick(false)"
                  >
                    <q-icon name="cross" />
                  </e-button>
                </div>
                <div class="filters-items flex justify-between flex-col">
                  <ul>
                    <li>
                      <div
                        class="filter-type"
                        tabindex="0"
                        @click="handleSubmenuClick('schoolLevel')"
                        @keydown.enter="handleSubmenuClick('schoolLevel')"
                        @keydown.space.prevent="handleSubmenuClick('schoolLevel')"
                      >
                        Kies schoolniveau
                        <span class="text-blue-500 icon-chevron">
                          <q-icon name="chevron-right-blue" />
                        </span>
                      </div>
                      <filter-modal
                        label="Kies schoolniveau"
                        label-multiple="schoolniveau"
                        :options="levels"
                        :selected-options="selectedLevels"
                        :loading="loadingLevels"
                        class="filter-levels"
                        :show="activeFilterSubmenu === 'schoolLevel'"
                        @update="(options) => startFiltering('levels', options)"
                        @close="handleSubmenuClick()"
                      />
                    </li>
                    <li>
                      <div
                        class="filter-type"
                        tabindex="0"
                        @click="handleSubmenuClick('schoolSubject')"
                        @keydown.enter="handleSubmenuClick('schoolSubject')"
                        @keydown.space.prevent="handleSubmenuClick('schoolSubject')"
                      >
                        Kies schoolvak
                        <span class="text-blue-500 icon-chevron">
                          <q-icon name="chevron-right-blue" />
                        </span>
                      </div>
                      <filter-modal
                        label="Kies schoolvak"
                        label-multiple="Schoolvak"
                        :options="subjects.sort()"
                        :selected-options="selectedSubjects"
                        :loading="loadingSubjects"
                        class="filter-subject"
                        :show="activeFilterSubmenu === 'schoolSubject'"
                        @update="(options) => startFiltering('subjects', options)"
                        @close="handleSubmenuClick()"
                      />
                    </li>
                    <li>
                      <div
                        class="filter-type"
                        tabindex="0"
                        @click="handleSubmenuClick('productType')"
                        @keydown.enter="handleSubmenuClick('productType')"
                        @keydown.space.prevent="handleSubmenuClick('productType')"
                      >
                        Kies producttype
                        <span class="text-blue-500 icon-chevron">
                          <q-icon name="chevron-right-blue" />
                        </span>
                      </div>
                      <filter-modal
                        label="Kies producttype"
                        label-multiple="Producttype"
                        :options="productTypes"
                        :selected-options="selectedProductTypes"
                        :loading="loadingProductTypes"
                        class="filter-product-type"
                        :show="activeFilterSubmenu === 'productType'"
                        @update="(options) => startFiltering('productTypes', options)"
                        @close="handleSubmenuClick()"
                      />
                    </li>
                  </ul>
                </div>
              </div>
              <div class="flex items-center justify-between gap-2 p-4 bg-white ">
                <e-button
                  type="secondary"
                  :disabled="!activeFilters"
                  class="w-full filter-clear-button"
                  @click="clear"
                >
                  Wis alle filters
                </e-button>
                <e-button
                  class="w-full"
                  @click="handleClick(false)"
                >
                  Toon resultaten
                </e-button>
              </div>
            </div>
          </div> <a
            class="flex items-center justify-center w-full px-16 py-2 mt-2 mb-6 text-xs text-blue-500 bg-blue-100 rounded-lg md:m-0 md:hidden whitespace-nowrap product-link"
            :href="link"
          > <q-icon name="blue-info" /> Welk product heb ik nodig?</a>

          <div class="hidden w-full gap-2 md:flex md:items-stretch">
            <dropdown-filter
              v-if="!hasDefaultFilter(filterDefinitions.educationType)"
              label="Kies schoolniveau"
              label-multiple="Schoolniveau"
              :options="levels"
              :selected-options="selectedLevels"
              :loading="loadingLevels"
              class="filter-levels"
              @update="setLevels"
            />
            <dropdown-filter
              v-if="!hasDefaultFilter(filterDefinitions.subject)"
              label="Kies schoolvak"
              label-multiple="Schoolvak"
              :options="subjects.sort()"
              :selected-options="selectedSubjects"
              :loading="loadingSubjects"
              class="filter-subject"
              @update="setSubjects"
            />
            <dropdown-filter
              v-if="!hasDefaultFilter(filterDefinitions.brand)"
              label="Kies producttype"
              label-multiple="Producttype"
              :options="productTypes"
              :selected-options="selectedProductTypes"
              :loading="loadingProductTypes"
              class="filter-product-type"
              @update="setProductTypes"
            />
            <a
              class="flex items-center justify-center px-16 text-xs text-blue-500 bg-blue-100 rounded-lg whitespace-nowrap"
              :href="link"
            > <q-icon name="blue-info" /> Welk product heb ik nodig?</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.filter-close-button {
  padding: 0 !important;
}

.product-filters.is-fixed {
  @apply fixed;
  @apply md:py-3 pb-4;
  box-shadow: 0 0.625rem 1.25rem rgba(0, 0, 0, 0.08);

  // we have a smaller vertical padding on the filters for screens of small height, and override it here
  @media screen and (min-height: 44em) {
    @apply md:py-6;
  }

  .product-link {
    @apply hidden;
  }
}

.filter-subject {
  .button-toggle {
    min-width: theme('spacing.36');
  }
}

.filter-product-type {
  .button-toggle {
    min-width: theme('spacing.40');
  }
}

.filter-button {
  @apply font-normal text-base py-3.5;
}

.filter-wrapper {
  z-index: 999
}

.filters-items {
  @apply px-4;

  li .filter-type {
    @apply relative flex w-full items-center outline-none text-black py-6 justify-between text-lg leading-6;
  }

  .icon-chevron .q-icon {
    svg {
      height: 14px !important;
      width: 8px !important;
    }
  }

}

.filter-subject, .filter-levels, .filter-product-type {
  width: 100% !important;
  > button.button-toggle {
    @apply p-3 rounded-lg border border-gray-500 ;
    box-shadow: none;
    width: 100% !important;
    > span {
      @apply w-full justify-between;
    }
    svg {
      @apply text-blue-500;
    }
  }
}

/* The desktop filter bar sizes all three controls with `width: 100%` and lets
   flex shrink them, so at the narrow end of the row the labels used to wrap:
   "Kies schoolniveau" broke onto a second line and made that button taller than
   its siblings, knocking the row out of alignment. DropdownFilter
   now truncates its label instead, but that only produces an ellipsis if the
   flex item may shrink below its single-line width — with the default
   `min-width: auto` the row overflows horizontally instead.

   Scoped to `.product-filters` on purpose: SubjectCardFilters reuses
   `.filter-subject`, and its pill-shaped dropdowns should keep sizing to their
   content rather than being shrinkable. */
.product-filters {
  .filter-subject, .filter-levels, .filter-product-type {
    min-width: 0;

    /* Overrides the 9rem/10rem floors set above, which would otherwise stop the
       buttons shrinking and reintroduce the overflow. */
    > button.button-toggle {
      min-width: 0;
    }
  }
}

.icon-blue-info svg {
  width: 10px;
  height: 10px;
}

</style>
